import type { ReactNode } from 'react';

import {
  Box,
  Card,
  CircularProgress,
  IconButton,
  useTheme,
} from '@mui/material';
import { XIcon } from 'lucide-react';

import { DragHandle } from '@modules/_shared/components/sortable-list/DragHandle';
import { useSortableItem } from '@modules/_shared/components/sortable-list/useSortableItem';
import { useLocale } from '@shared/hooks/locale';

type SortableListingCardProps = {
  id: string;
  index: number;
  offline?: boolean;
  loading?: boolean;
  onRemove: () => void | Promise<void>;
  children: ReactNode;
};

export const SortableListingCard = ({
  id,
  index,
  offline = false,
  loading = false,
  onRemove,
  children,
}: SortableListingCardProps) => {
  const { t } = useLocale();
  const { setNodeRef, style, isDragging, dragHandleProps } =
    useSortableItem(id);
  const theme = useTheme();
  const palette = (theme.vars ?? theme).palette;

  return (
    <Box
      ref={setNodeRef}
      style={{
        ...style,
        position: 'relative',
        cursor: isDragging ? 'grabbing' : 'pointer',
      }}
    >
      {!isDragging && (
        <Box
          sx={{
            position: 'absolute',
            top: -12,
            left: -12,
            height: 30,
            width: 30,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontWeight: 600,
            borderRadius: '50%',
            color: '#fff',
            background: offline ? palette.error.main : palette.success.main,
            border: '3px solid',
            borderColor: 'background.paper',
            zIndex: 1,
          }}
        >
          {index + 1}
        </Box>
      )}
      <Card
        data-ignore-interaction
        sx={{
          display: 'flex',
          flexDirection: 'row',
          position: 'relative',
          background: 'var(--mui-palette-background-paper)',
          border: '1px solid',
          borderColor: 'divider',
        }}
        elevation={0}
      >
        <Box
          sx={{
            position: 'absolute',
            bottom: 0,
            right: 2,
            paddingBottom: '2px',
            display: 'inline-flex',
          }}
        >
          <DragHandle
            attributes={dragHandleProps.attributes}
            listeners={dragHandleProps.listeners}
            isDragging={isDragging}
            variant="icon-button"
            rotateIcon
          />
        </Box>
        <Box
          sx={{
            position: 'absolute',
            top: 2,
            right: 2,
            padding: 0,
            display: 'inline-flex',
          }}
        >
          <IconButton
            aria-label={t('Remove')}
            size="small"
            onClick={async evt => {
              evt.stopPropagation();
              evt.preventDefault();
              await onRemove();
            }}
            onMouseDown={evt => {
              evt.stopPropagation();
            }}
            disabled={loading}
          >
            {loading ? (
              <CircularProgress
                sx={{ color: palette.text.secondary, opacity: 0.5 }}
                size={20}
              />
            ) : (
              <XIcon size={20} />
            )}
          </IconButton>
        </Box>
        {children}
      </Card>
    </Box>
  );
};
